import {
  ClassificationType,
  EventsSummary,
  ClassificationDetails,
} from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import React, { useMemo, useState } from 'react'
import { createCustomContext } from 'utils/hooks/createCustomContext'
import { useEventsSummary } from 'api/dataSentry/queries/useEventsSummary'
import { useSystemsSummary } from 'api/dataSentry/queries/useSystemsSummary'
import {
  DataSystemDetails,
  SystemsSummary,
  AuthorizationStatus,
} from '@ketch-com/windlass/dist/dataSentry/v1/systems/systems'
import { PropertyOption, TimeRangeOption } from '../utils/types'
import { getPropertyCode } from '../utils/helpers'
import { useMostSeenTrackers } from '../trackers/hooks/useMostSeenTrackers'
import { MostSeenTrackerItemProps } from '../trackers/mostSeen/MostSeenTrackerItem'
import { useDataSentryDataSystemsList } from 'api/dataSentry/queries/useDataSentryDataSystemsList'
import { useEventsBySystem } from 'api/dataSentry/queries/useDataSentryEventsBySystem'
import { ScanHistory, ScanUrlList } from '@ketch-com/windlass/dist/dataSentry/v1/scans/scan'
import { useDataSentryScanHistory } from 'api/dataSentry/queries/useDataSentryScanHistory'
import { useDataSentryScanUrls } from 'api/dataSentry/queries/useDataSentryScanUrls'
import { useTrackersSummary } from 'api/dataSentry/queries/useTrackersSummary'
import {
  TrackersSummary,
  AuthorizationStatus as TrackerAuthorizationStatus,
  ControlStatus as TrackerControlStatus,
  TrackerListItem,
  TrackersList,
} from '@ketch-com/windlass/dist/dataSentry/v1/trackers/trackers'
import { useClassificationDetails } from 'api/dataSentry/queries/useClassificationDetails'
import { DataClassificationDetails } from 'pages/dataSentry/interfaces'

export interface TopSystem {
  name?: string
  code?: string
  logo?: string
  collectionEvents?: number
  trackerCount?: number
  usheredBy?: DataSystemDetails[]
  dataTypeCount?: number
}

export interface DataSentryContextValues {
  systemsSummary: SystemsSummary | null
  eventsSummary: EventsSummary | null
  isEventsSummaryLoading: boolean
  isSystemsLoading: boolean
  selectedProperty: PropertyOption | null
  selectedTimeRange: TimeRangeOption | null
  setSelectedProperty: React.Dispatch<React.SetStateAction<PropertyOption | null>>
  setSelectedTimeRange: React.Dispatch<React.SetStateAction<TimeRangeOption | null>>
  selectedPropertyCode?: string
  mostSeenTrackers: MostSeenTrackerItemProps[]
  mostSeenTrackersLoading?: boolean
  mostSeenTrackersAuthorizationStatus?: TrackerAuthorizationStatus
  setMostSeenTrackersAuthorizationStatus?: React.Dispatch<React.SetStateAction<TrackerAuthorizationStatus>>
  mostSeenTrackersControlStatus?: TrackerControlStatus
  setMostSeenTrackersControlStatus?: React.Dispatch<React.SetStateAction<TrackerControlStatus>>
  topSystems: TopSystem[]
  isTopSystemsLoading?: boolean
  topUnauthorizedSystems: TopSystem[]
  isTopUnauthorizedSystemsLoading?: boolean
  filteredDataSystemListOptions: DataSystemDetails[]
  isLoadingDataSystemListOptions?: boolean
  dataTypesTotalCount?: number
  scanHistory: ScanHistory | null
  isLoadingScanHistory: boolean
  scannedUrls: ScanUrlList | null
  isLoadingScannedUrls: boolean
  trackersSummary?: TrackersSummary | null
  isTrackersSummaryLoading?: boolean
  trackerOverview: TrackerListItem | null
  setTrackerOverview: React.Dispatch<React.SetStateAction<TrackerListItem | null>>
  trackersList?: TrackersList
  classificationDetails?: ClassificationDetails
  isClassificationDetailsLoading?: boolean
  dataClassificationDetails?: DataClassificationDetails | null
  setDataClassificationDetails: React.Dispatch<React.SetStateAction<DataClassificationDetails | null>>
}

const { Provider, useCustomContext: useDataSentryContext } = createCustomContext<DataSentryContextValues>({
  displayName: 'DataSentryContext',
})

export const DataSentryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedProperty, setSelectedProperty] = useState<PropertyOption | null>(null)
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRangeOption | null>(null)
  const [trackerOverview, setTrackerOverview] = useState<TrackerListItem | null>(null)
  const [dataClassificationDetails, setDataClassificationDetails] = useState<DataClassificationDetails | null>(null)

  const selectedPropertyCode = useMemo(() => getPropertyCode(selectedProperty), [selectedProperty])

  //TODO: go through the rest of the items in context, check if they're used more than once, remove from context if not
  //TODO: go through apis and rename fetchers and hooks to describe the data they get, not a sepecific case where that data is used

  //events summary for stats
  const { data: eventsSummary, isLoading: isEventsSummaryLoading } = useEventsSummary({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
    },
  })

  //systems summary for stats
  const { data: systemsSummary, isLoading: isSystemsLoading } = useSystemsSummary({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedProperty?.code,
    },
  })

  const { data: trackersSummary, isLoading: isTrackersSummaryLoading } = useTrackersSummary({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedProperty?.code,
    },
  })

  const {
    mostSeenTrackers,
    isLoading: mostSeenTrackersLoading,
    setTrackerAuthorizationStatus: setMostSeenTrackersAuthorizationStatus,
    trackerAuthorizationStatus: mostSeenTrackersAuthorizationStatus,
    trackerControlStatus: mostSeenTrackersControlStatus,
    setTrackerControlStatus: setMostSeenTrackersControlStatus,
    trackersList,
  } = useMostSeenTrackers({
    startDate: selectedTimeRange?.startDate,
    endDate: selectedTimeRange?.endDate,
    selectedPropertyCode,
  })

  const { data: classificationDetails, isLoading: isClassificationDetailsLoading } = useClassificationDetails({
    params: {
      'classification-type': dataClassificationDetails?.type,
      'classification-code': dataClassificationDetails?.code,
    },
    enabled: Boolean(dataClassificationDetails),
  })

  const { data: topSystemsData, isLoading: isTopSystemsLoading } = useEventsBySystem({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedProperty?.code,
      limit: 5,
      offset: 0,
    },
  })

  const { data: topUnauthorizedSystemsData, isLoading: isTopUnauthorizedSystemsLoading } = useEventsBySystem({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedProperty?.code,
      limit: 5,
      offset: 0,
      'authorization-status': AuthorizationStatus.UNAUTHORIZED,
    },
  })

  const { data: dataSystemListOptions, isLoading: isLoadingDataSystemListOptions } = useDataSentryDataSystemsList({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedProperty?.code,
    },
  })

  const { data: scanHistory, isLoading: isLoadingScanHistory } = useDataSentryScanHistory({
    params: {
      'property-codes': selectedProperty?.code,
    },
  })

  const { data: scannedUrls, isLoading: isLoadingScannedUrls } = useDataSentryScanUrls({
    params: {
      'property-codes': selectedProperty?.code,
      limit: 10,
      offset: 0,
      query: '',
    },
  })

  const filteredDataSystemListOptions = useMemo(() => {
    if (!dataSystemListOptions?.dataSystems?.length) {
      return []
    }
    return dataSystemListOptions.dataSystems.filter(system => !!system.name)
  }, [dataSystemListOptions])

  const topSystems = useMemo(() => {
    if (!topSystemsData?.dataSystems?.length) {
      return []
    }
    const totalCount = topSystemsData?.total
    return topSystemsData.dataSystems.map(system => {
      const dataTypes =
        system.classificationsByType?.find(cls => cls.classificationType === ClassificationType.DATA_TYPE)
          ?.classifications || []

      return {
        name: system.dataSystem?.name,
        code: system.dataSystem?.code,
        logo: system.dataSystem?.logoUrl,
        collectionEvents: system.count,
        trackerCount: system.trackerCount,
        totalCount,
        dataTypeCount: dataTypes.length,
        usheredBy: system.usheredBy || [],
      }
    })
  }, [topSystemsData])

  const topUnauthorizedSystems = useMemo(() => {
    if (!topUnauthorizedSystemsData?.dataSystems?.length) {
      return []
    }
    const totalCount = topUnauthorizedSystemsData?.total
    return topUnauthorizedSystemsData.dataSystems.map(system => {
      const dataTypes =
        system.classificationsByType?.find(cls => cls.classificationType === ClassificationType.DATA_TYPE)
          ?.classifications || []

      return {
        name: system.dataSystem?.name,
        code: system.dataSystem?.code,
        logo: system.dataSystem?.logoUrl,
        collectionEvents: system.count,
        trackerCount: system.trackerCount,
        totalCount,
        dataTypeCount: dataTypes.length,
        usheredBy: system.usheredBy || [],
      }
    })
  }, [topUnauthorizedSystemsData])

  const dataTypesTotalCount = useMemo(() => {
    return eventsSummary?.classificationTypes?.find(type => type.classificationType === ClassificationType.DATA_TYPE)
      ?.totalClassificationCount
  }, [eventsSummary?.classificationTypes])

  // Return value for the context defined here
  const value = useMemo(
    () => ({
      systemsSummary,
      eventsSummary,
      isSystemsLoading,
      isEventsSummaryLoading,
      selectedProperty,
      setSelectedProperty,
      selectedTimeRange,
      setSelectedTimeRange,
      selectedPropertyCode,
      mostSeenTrackers,
      mostSeenTrackersLoading,
      mostSeenTrackersAuthorizationStatus,
      setMostSeenTrackersAuthorizationStatus,
      mostSeenTrackersControlStatus,
      setMostSeenTrackersControlStatus,
      topSystems,
      isTopSystemsLoading,
      filteredDataSystemListOptions,
      isLoadingDataSystemListOptions,
      dataTypesTotalCount,
      scanHistory,
      isLoadingScanHistory,
      scannedUrls,
      isLoadingScannedUrls,
      trackersSummary,
      isTrackersSummaryLoading,
      topUnauthorizedSystems,
      isTopUnauthorizedSystemsLoading,
      trackerOverview,
      setTrackerOverview,
      trackersList,
      classificationDetails,
      isClassificationDetailsLoading,
      dataClassificationDetails,
      setDataClassificationDetails,
    }),
    [
      systemsSummary,
      eventsSummary,
      isSystemsLoading,
      isEventsSummaryLoading,
      selectedProperty,
      selectedTimeRange,
      selectedPropertyCode,
      mostSeenTrackers,
      mostSeenTrackersLoading,
      mostSeenTrackersAuthorizationStatus,
      setMostSeenTrackersAuthorizationStatus,
      mostSeenTrackersControlStatus,
      setMostSeenTrackersControlStatus,
      topSystems,
      isTopSystemsLoading,
      filteredDataSystemListOptions,
      isLoadingDataSystemListOptions,
      dataTypesTotalCount,
      scanHistory,
      isLoadingScanHistory,
      scannedUrls,
      isLoadingScannedUrls,
      trackersSummary,
      isTrackersSummaryLoading,
      topUnauthorizedSystems,
      isTopUnauthorizedSystemsLoading,
      trackerOverview,
      setTrackerOverview,
      trackersList,
      classificationDetails,
      isClassificationDetailsLoading,
      dataClassificationDetails,
      setDataClassificationDetails,
    ],
  )

  return <Provider value={value}>{children}</Provider>
}

export { useDataSentryContext }
